import {
  Component,
  computed,
  effect,
  inject,
  Signal,
  ViewChild,
} from '@angular/core';
import { ConfigurationStore } from '../../../services/api/configuration/configuration-store.service';
import { Router, RouterOutlet } from '@angular/router';
import {
  IProgressBarStep,
  TicketConfigurationProgressTrackerComponent,
} from '../../../components/feedback/ticket-configuration-progress-tracker/ticket-configuration-progress-tracker.component';
import {
  AUTHORIZE_PAYMENT,
  CONFIGURATION_SETTING_KEYS,
  DIRECT_TICKET_CART_KEY,
  EEntityType,
  ERelationType,
  IConfiguredDirectTicket,
  ShopTicket,
} from '@web/dtos';
import { ShoppingCartService } from '../../../services/shopping-cart/shopping-cart.service';
import {
  ITicketDefinitionForm,
  ShoppingCartFormService,
} from '../../../services/shopping-cart/shopping-cart-form.service';
import { ActiveDirectTicketStore } from '../../../services/api/direct-ticket/active/active-direct-ticket-store.service';
import { FormGroup } from '@angular/forms';
import { IRequiredTicketOwnerData } from './direct-ticket-owner-data-page/direct-ticket-owner-data-page.component';
import { ProgressTrackerSupportService } from '../../../services/support/progress-tracker-support/progress-tracker-support.service';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';
import { FilterTextsPipe } from '../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../pipes/language/language.pipe';
import { ERoute } from '../../../constants/routes.enum';
import { AuthProviderFactory } from '../../../services/auth/auth-provider.factory';
import { AuthProvider } from '../../../services/auth/auth.provider';

@Component({
  selector: 'aw-direct-ticket-configuration-page',
  imports: [TicketConfigurationProgressTrackerComponent, RouterOutlet],
  templateUrl: './direct-ticket-configuration-page.component.html',
})
export class DirectTicketConfigurationPageComponent {
  private readonly auth: AuthProvider =
    inject(AuthProviderFactory).getProvider('shop');

  private readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  private readonly shoppingCart: ShoppingCartService =
    inject(ShoppingCartService);
  private readonly shoppingCartFormService: ShoppingCartFormService = inject(
    ShoppingCartFormService
  );
  private readonly directTicketStore: ActiveDirectTicketStore = inject(
    ActiveDirectTicketStore
  );
  private readonly textStore: WebTextStore = inject(WebTextStore);
  private readonly router: Router = inject(Router);
  private readonly progressTrackerService: ProgressTrackerSupportService =
    inject(ProgressTrackerSupportService);

  private readonly textFilterPipe: FilterTextsPipe = new FilterTextsPipe();
  private readonly languagePipe: LanguagePipe = new LanguagePipe();

  @ViewChild('progressTracker')
  progressTracker!: TicketConfigurationProgressTrackerComponent;

  protected readonly ERoute: typeof ERoute = ERoute;

  private readonly ticketConfig: string = 'ticket-configuration/';
  private readonly options: string = 'options/';
  private readonly personalData: string = 'personal-data';
  private readonly ownerData: string = 'ticket-owner-data';
  private readonly checkout: string = 'checkout';
  private readonly payment: string = 'payment';
  private cart:
    | { products: IConfiguredDirectTicket[]; personalData: any }
    | undefined = undefined;

  public readonly ticketConfiguration: Signal<ShopTicket> = this.shoppingCart
    .directTicketConfiguration as Signal<ShopTicket>;

  protected progressBar: Signal<IProgressBarStep[]> = computed(() =>
    this.ticketConfiguration() ? this.buildDirectTicketConfigProgressBar() : []
  );

  constructor() {
    this.progressTrackerService.currentStep.set(0);
    const cartInLocalStorage: string | null = window.localStorage.getItem(
      DIRECT_TICKET_CART_KEY
    );
    const redirectFromAuthorize: string | null =
      window.localStorage.getItem(AUTHORIZE_PAYMENT);
    effect((): void => {
      const isLoading: boolean = this.configurationStore.isLoading();
      const isLoginOK: boolean =
        this.auth.isLoggedIn() ||
        this.configurationStore.getBooleanConfiguration(
          CONFIGURATION_SETTING_KEYS.IsDirectSaleGuestActive
        );
      const isDirectSaleForbidden: boolean =
        !this.configurationStore.getBooleanConfiguration(
          CONFIGURATION_SETTING_KEYS.IsDirectSaleActive
        ) && !this.configurationStore.isLoading();
      const currentStep: number | undefined = this.progressTracker?.step();
      const isIllegalStepWithNoTicket: boolean =
        (currentStep < 0 || currentStep > 1) &&
        this.shoppingCart.productsInCart().length === 0 &&
        !cartInLocalStorage;

      if (
        (!isLoading && !cartInLocalStorage && !isLoginOK) ||
        isDirectSaleForbidden ||
        isIllegalStepWithNoTicket
      ) {
        this.router.navigate(['/']);
      } else if (!this.configurationStore.isLoading() && cartInLocalStorage) {
        try {
          this.cart = JSON.parse(cartInLocalStorage);
          this.cart!.products.forEach((product: IConfiguredDirectTicket) => {
            this.shoppingCart.productForms.controls.unshift(
              this.shoppingCartFormService.createProductForm(
                product.tariffProductGroup.selectionAttributes.filter(
                  attribute => attribute.name.toLowerCase() !== 'name'
                ),
                product.tariffProductGroup.selectionAttributes.filter(
                  attribute => attribute.name.toLowerCase() === 'name'
                ),
                {
                  ...product.product,
                  additionalEntries: product.additionalEntries ?? [],
                } as FormGroup<ITicketDefinitionForm>['value']
              )
            );
            this.shoppingCart.ticketOwnerDataForms.controls.unshift(
              this.shoppingCartFormService.createTicketOwnerForms(
                product.owners as IRequiredTicketOwnerData[]
              )
            );
            this.shoppingCart.productForms.updateValueAndValidity();
            this.shoppingCart.ticketOwnerDataForms.updateValueAndValidity();

            this.shoppingCart.productsInCart.set(
              this.cart!.products!.map(
                (product: IConfiguredDirectTicket, index: number) => {
                  return {
                    ...product,
                    tariffProductGroup: product.tariffProductGroup,
                    ticket: product.ticket,
                    productForm: this.shoppingCart.productForms.controls[index],
                    owner:
                      this.shoppingCart.ticketOwnerDataForms.controls[index],
                  };
                }
              ) ?? []
            );
          });
          this.shoppingCart.personalDataForm.setValue(this.cart!.personalData);
          this.shoppingCart.setCurrentProductDefinition(
            this.cart!.products[0].product.product,
            this.cart!.products[0].product.selectionAttributes!,
            this.cart!.products[0].product.productSortOptions ?? []
          );
          this.directTicketStore.loadTicketConfiguration(
            this.cart!.products[0].name
          );
          localStorage.removeItem(DIRECT_TICKET_CART_KEY);
        } catch (error) {
          localStorage.removeItem(DIRECT_TICKET_CART_KEY);
          localStorage.removeItem(AUTHORIZE_PAYMENT);
          this.router.navigate(['/']);
        }
      } else if (
        this.shoppingCart.productForms.controls.length === 0 &&
        !cartInLocalStorage &&
        !redirectFromAuthorize
      ) {
        this.router.navigate(['/']);
      }
    });
    setTimeout(() => window.scrollTo(0, 0), 0); //scroll to top when starting configuration
  }

  private buildDirectTicketConfigProgressBar(): IProgressBarStep[] {
    const startingSteps: Pick<IProgressBarStep, 'label' & 'url'>[] = [
      {
        label: this.languagePipe.transform(
          this.textFilterPipe.transform(
            this.textStore.texts(),
            ERelationType.ticketOptions,
            EEntityType.directTicket
          )
        ),
        url:
          this.ticketConfig +
          this.options +
          this.shoppingCart.directTicketConfiguration()!.name,
      },
      {
        label: this.languagePipe.transform(
          this.textFilterPipe.transform(
            this.textStore.texts(),
            ERelationType.personalData,
            EEntityType.generic
          )
        ),
        url: this.ticketConfig + this.personalData,
      },
    ];
    const optionalPassengerData: Pick<IProgressBarStep, 'label' & 'url'>[] =
      this.shoppingCart.isTicketOwnerDataRequired()
        ? [
            {
              label: this.languagePipe.transform(
                this.textFilterPipe.transform(
                  this.textStore.texts(),
                  ERelationType.passengerData,
                  EEntityType.directTicket
                )
              ),
              url: this.ticketConfig + this.ownerData,
            },
          ]
        : [];
    const lastSteps: Pick<IProgressBarStep, 'label' & 'url'>[] = [
      {
        label: this.languagePipe.transform(
          this.textFilterPipe.transform(
            this.textStore.texts(),
            ERelationType.checkout,
            EEntityType.directTicket
          )
        ),
        url: this.ticketConfig + this.checkout,
      },
      {
        label: this.languagePipe.transform(
          this.textFilterPipe.transform(
            this.textStore.texts(),
            ERelationType.payment,
            EEntityType.directTicket
          )
        ),
        url: this.ticketConfig + this.payment,
      },
    ];
    return startingSteps
      .concat(optionalPassengerData)
      .concat(lastSteps)
      .map((step: Pick<IProgressBarStep, 'label' & 'url'>, index: number) => {
        return {
          ...step,
          stepIndex: index,
        } as IProgressBarStep;
      });
  }
}
